import type { ReactNode } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';
import { cn } from '@nucleus-commerce/ui/lib/utils';

interface FieldProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  description?: ReactNode;
  disabled?: boolean;
}

/** A labelled switch in a bordered row, the settings screens' on/off control. */
export function SwitchField<T extends FieldValues>({
  control,
  name,
  label,
  description,
  disabled,
}: FieldProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-row items-center justify-between gap-4 rounded-md border p-3">
          <div className="grid gap-1">
            <FormLabel>{label}</FormLabel>
            {description ? <FormDescription>{description}</FormDescription> : null}
          </div>
          <FormControl>
            <Switch
              checked={Boolean(field.value)}
              onCheckedChange={field.onChange}
              onBlur={field.onBlur}
              disabled={disabled}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
}

interface TextFieldProps<T extends FieldValues> extends FieldProps<T> {
  placeholder?: string;
  /** A whole number typed as text; `suffix` names the unit. */
  numeric?: boolean;
  suffix?: string;
  multiline?: boolean;
  className?: string;
  mono?: boolean;
}

export function TextField<T extends FieldValues>({
  control,
  name,
  label,
  description,
  disabled,
  placeholder,
  numeric = false,
  suffix,
  multiline = false,
  className,
  mono = false,
}: TextFieldProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel>{label}</FormLabel>
          <div className="flex items-center gap-2">
            <FormControl>
              {multiline ? (
                <Textarea rows={2} disabled={disabled} placeholder={placeholder} {...field} />
              ) : (
                <Input
                  inputMode={numeric ? 'numeric' : undefined}
                  disabled={disabled}
                  placeholder={placeholder}
                  className={cn(numeric && 'w-28', mono && 'font-mono')}
                  {...field}
                />
              )}
            </FormControl>
            {suffix ? <span className="text-muted-foreground text-sm">{suffix}</span> : null}
          </div>
          {description ? <FormDescription>{description}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/** The tab's own server error (409 closed store, 403, a field the form does not show). */
export function FormAlert({ message }: { message: string | undefined }) {
  if (!message) return null;
  return (
    <Alert variant="destructive" role="alert">
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

/** A visible Save at the bottom of a long tab; the save bar does the same. */
export function SaveButton({
  saving,
  dirty,
  hidden,
}: {
  saving: boolean;
  dirty: boolean;
  hidden: boolean;
}) {
  const { t } = useTranslation(['chatSettings', 'common']);
  if (hidden) return null;
  return (
    <div>
      <Button type="submit" disabled={saving || !dirty}>
        {saving ? t('common:actions.saving') : t('common:actions.save')}
      </Button>
    </div>
  );
}
